import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { App, Button, Divider, Input, Modal, Tooltip } from 'antd';
import { CloseIcon, ContactImage, NewCancelIcon, NewUpdateIcon } from '../../Common/Icons';
import ContactSelectionForStaff from './ContactSelectionForStaff';
import './NewAssignStaff.less';
import Roles from '../../Roles/Roles';
import { PhotoResolution, RoleType } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  getProfilePicUrl,
  getUsername,
  objectSort,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { getProfilePics, getUserInfo } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import useUserInfo from '../../Hooks/useUserInfo';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';

const AssignStaffModal = ({
  isFirstModalOpen,
  handleCloseFirstModal,
  handleAddStaff,
  mOrderedStaffList,
  fetchingStaffList,
  handleRoleSelection,
  selectedUser = '',
  selectedStaff,
  handleAssignStaff,
  handleOpenSecondModal,
  handleCloseSecondModal,
  handleInputChange: parentHandleInputChange,
  isSecondModalOpen,
  setSelectedStaff,
  role,
  hubRecord,
  setSearchNameApplied,
  searchNameApplied,
  setSearchNameText,
  searchNameText,
  linkAccountChecked = false,
  setLinkAccountChecked,
  userHubsData,
  onActionClick,
  contactsList = [],
  fetchingContactsList,
}) => {
  const { message } = App.useApp(); // Use App context for message API
  const currentUser = useSelector(getCurrentUser);
  const userApi = useSelector((state) => state.apis.userApi);
  const trans = useSelector(translator);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    sourcerelationship: '',
    targetrelationship: '',
  });
  // Filter contacts list based on current user permission level and exclude contact members in accountList
  const filteredAccountList = useMemo(() => {
    // First, exclude any contacts members whose useruuid is in contactsList
    const accountContactUuids = (contactsList || []).map((contact) => contact.useruuid);
    const AccountsListWithoutAccountContacts = mOrderedStaffList.filter(
      (staff) => !accountContactUuids.includes(staff.useruuid)
    );

    // Then apply the permission-based filtering
    let filteredList = AccountsListWithoutAccountContacts;
    if (currentUser?.permission_level === 'Staff Admin') {
      filteredList = AccountsListWithoutAccountContacts;
    } else {
      filteredList = AccountsListWithoutAccountContacts.filter(
        (staff) => staff.rolename === 'Manage'
      );
    }

    // Apply the hubRecord.useruuid check
    const hubRecordUserUuid = hubRecord?.useruuid;
    if (hubRecordUserUuid) {
      filteredList = filteredList.filter((staff) => staff.useruuid !== hubRecordUserUuid);
    }

    return filteredList;
  }, [
    mOrderedStaffList,
    currentUser?.permission_level,
    contactsList,
    hubRecord?.useruuid,
    linkAccountChecked,
  ]);

  const sortedAccountsList = useMemo(() => {
    return [...filteredAccountList].sort(objectSort('firstname'));
  }, [filteredAccountList]);

  const handleInputChange = (e) => {
    if (typeof parentHandleInputChange === 'function') {
      parentHandleInputChange(e);
    }
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const { data: userInfo = {} } = useUserInfo({
    useruuid: selectedStaff?.useruuid,
  });

  // State to track hover
  const [isHovered, setIsHovered] = useState(false);

  // Determine the button text based on selection state and hover
  const displayButtonText = () => {
    if (selectedStaff) {
      return processing ? trans('PROCESSING') : trans('ADD_CONTACT_L');
    } else {
      return isHovered ? trans('SELECT') : trans('UPDATE');
    }
  };
  const formatData = (data, selectedStaff) => {
    // Format according to the required structure
    return {
      rolename: 'Social',
      firstname: selectedStaff?.firstname || '',
      lastname: selectedStaff?.lastname || '',
      email: userInfo?.email || '',
      sourcerelationship: data.sourcerelationship || '',
      targetrelationship: data.targetrelationship || '',
    };
  };

  const handleSubmit = () => {
    const errorCallback = (error) => {
      setProcessing(false);
      // message.error(`Error linking account`);
      console.error(`Error linking account`);
    };

    const successCallback = (data) => {
      setProcessing(false);
      // message.success(`Account Linked successfully!`);
      handleCloseFirstModal();
      resetFormState();
      setLinkAccountChecked(false);
      onActionClick(null, null, true)();
    };

    try {
      // Validate required fields
      if (!selectedStaff?.useruuid) {
        // message.error("Selected staff information is missing");
        console.error('Selected staff information is missing');
        return;
      }

      // Safely trim values with null checks
      const trimmedFormData = {
        sourcerelationship: formData.sourcerelationship ? formData.sourcerelationship.trim() : '',
        targetrelationship: formData.targetrelationship ? formData.targetrelationship.trim() : '',
      };

      setProcessing(true);

      // Format the data according to the API requirements
      const apiPayload = formatData(trimmedFormData, selectedStaff);

      // Call the API with the formatted payload
      userApi.inviteContactsToUser(
        hubRecord?.useruuid,
        apiPayload,
        apiCallback({
          translator: trans,
          successCallback,
          errorCallback,
          failCallback: errorCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const truncateName = (name, length = 20) => {
    if (!name) return '';
    return name.length > length ? `${name.substring(0, length)}...` : name;
  };
  const renderFormItem = ({ name, label, disabled, placeholder, className }) => (
    <div className={`form-item form-item-verticle ${className ?? ''}`}>
      <label className="assign-staff-relationship-title">{label}</label>
      <div className="assign-staff-form-input-box">
        <Input
          className="assign-staff-input-content"
          name={name}
          placeholder={placeholder}
          value={formData[name]}
          onChange={handleInputChange}
          disabled={disabled}
        />
      </div>
    </div>
  );

  // Render role selection
  const renderRole = ({ label, className }) => {
    return (
      <div className={`form-item form-item-verticle ${className ?? ''}`}>
        <label>{label}</label>
        <Roles
          contact={true}
          type={RoleType.user}
          onHandleSelection={handleRoleSelection}
          isAssignStaff={true}
          currentRole={role}
          key={role}
        />
      </div>
    );
  };

  const AssignStaffHeaderModal = () => {
    return (
      <div className="assign-staff-modal-container">
        <div className="assign-staff-modal-header-title">
          <span className="assign-staff-modal-title">
            {linkAccountChecked ? trans('LINKACCOUNT') : trans('ASSIGN_STAFF_L')}
          </span>
        </div>
        <div
          className="assign-staff-modal-close-button"
          onClick={isSecondModalOpen ? handleCloseSecondModal : closeFirstModal}
        >
          <CloseIcon className="assign-staff-modal-close-icon" />
        </div>
      </div>
    );
  };

  const resourcesMap = useSelector(getProfilePics());
  const url = getProfilePicUrl(selectedStaff?.useruuid, resourcesMap, PhotoResolution.R64);
  const image =
    (selectedStaff?.profilePics && selectedStaff?.profilePics[PhotoResolution.R64]) ?? url;

  const AssignStaffFormItemModal = () => {
    return (
      <>
        <div className="assign-staff-select-contact">
          <div className="assign-staff-contact-block">
            <div className="selected-assign-staff-profile">
              {image ? (
                <img src={image} className="user-profile-pic user-profile-image" alt="pic" />
              ) : (
                <ContactImage className="photo-container" />
              )}
            </div>
            <div className="selected-assign-staff-name-role-container">
              <span className="selectedcontact">{selectedStaff && getUsername(selectedStaff)}</span>
              <span className="rolename">{selectedStaff && selectedStaff?.staffrole}</span>
            </div>
          </div>
        </div>
        <Divider className="assign-staff-divider" />
        <div className="assign-staff-contact-details-container">
          <div className="assign-staff-form-container">
            <div className="assign-staff-form-row">
              {renderFormItem({
                name: 'targetrelationship',
                label: (
                  <>
                    <span>
                      {selectedStaff && (
                        <span>{`${selectedStaff?.firstname}’${trans('S_L')} `}</span>
                      )}
                      {trans('RELATIONSHIPWITH')}
                      <Tooltip title={selectedUser.length > 20 ? selectedUser : ''}>
                        <span>{` ${truncateName(selectedUser)}`}</span>
                      </Tooltip>
                    </span>
                  </>
                ),
              })}
            </div>
            <div className="assign-staff-form-row">
              {renderFormItem({
                name: 'sourcerelationship',
                label: (
                  <>
                    <span>
                      <Tooltip title={`${hubRecord?.firstname} ${hubRecord?.lastname}`}>
                        {selectedUser && <span>{`${truncateName(selectedUser)}’s `}</span>}
                      </Tooltip>
                      {selectedStaff && (
                        <span>{`${trans('RELATIONSHIPWITH')} ${selectedStaff?.firstname}`}</span>
                      )}
                    </span>
                  </>
                ),
              })}
            </div>
          </div>
          <div className="assign-staff-role-container">
            <span className="assign-staff-role-title">
              {`${trans('WHATROLEDOESTHISSTAFFMEMBERHAVEWITH')} ${selectedUser}?*`}
            </span>
            <div className="roles-list">{renderRole({})}</div>
          </div>
        </div>
      </>
    );
  };
  const resetFormState = () => {
    // Reset staff selection
    setSelectedStaff(null);

    // Reset form data
    setFormData({
      sourcerelationship: '',
      targetrelationship: '',
    });
  };
  const closeFirstModal = () => {
    handleCloseFirstModal();
    if (linkAccountChecked) {
      setLinkAccountChecked(false);
    }
    resetFormState();
  };

  // Check if the update button should be disabled
  const isUpdateButtonDisabled = linkAccountChecked
    ? !selectedStaff || processing
    : !selectedStaff || processing;

  // Handle update button click based on linkAccountChecked
  const handleUpdateButtonClick = () => {
    if (linkAccountChecked) {
      handleSubmit();
    } else {
      handleOpenSecondModal();
    }
  };

  const AssignStaffModalFooter = ({ onClick, isDisabled, buttonText, processing }) => {
    return (
      <div className="assign-staff-modal-footer">
        <Button
          className={`${linkAccountChecked ? 'link-account-cancel-btn' : ''} cancel-button`}
          onClick={isSecondModalOpen ? handleCloseSecondModal : closeFirstModal}
          disabled={processing}
        >
          <NewCancelIcon />
          <span className="cancel-text">{trans('CANCEL')}</span>
        </Button>
        <Button
          onClick={onClick}
          disabled={isDisabled}
          className={`
          submit-button 
          ${isDisabled ? 'disabled-button' : ''} 
          ${(isDisabled || processing) && linkAccountChecked ? 'link-acc-btn-disabled' : ''}
        `}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <NewUpdateIcon
            className={`
            submit-icon 
            ${isDisabled ? 'disabled-icon' : ''} 
            ${(isDisabled || processing) && linkAccountChecked ? 'link-acc-icon-disabled' : ''}
          `}
          />
          <span
            className={`
            submit-text 
            ${isDisabled ? 'disabled-text' : ''} 
            ${(isDisabled || processing) && linkAccountChecked ? 'link-acc-text-disabled' : ''}
          `}
          >
            {buttonText}
          </span>
        </Button>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={isFirstModalOpen}
        onCancel={handleCloseFirstModal}
        footer={null}
        closable={false}
        centered
        className={`assign-staff-modal-main first ${
          linkAccountChecked ? 'linkaccountenabled' : ''
        }`}
        style={{ width: linkAccountChecked ? '681px' : '554px' }}
      >
        {AssignStaffHeaderModal()}
        <ContactSelectionForStaff
          fetchingContacts={fetchingStaffList}
          selectedStaff={selectedStaff}
          selectionContactList={sortedAccountsList}
          onAddStaff={handleAddStaff}
          selectedUser={selectedUser}
          setSelectedStaff={setSelectedStaff}
          hubRecord={hubRecord}
          setSearchNameApplied={setSearchNameApplied}
          searchNameApplied={searchNameApplied}
          setSearchNameText={setSearchNameText}
          searchNameText={searchNameText}
          linkAccountChecked={linkAccountChecked}
          formData={formData}
          handleInputChange={handleInputChange}
          currentUser={currentUser}
        />
        <AssignStaffModalFooter
          onClick={handleUpdateButtonClick}
          isDisabled={isUpdateButtonDisabled}
          processing={processing}
          buttonText={linkAccountChecked ? displayButtonText() : trans('SELECT_STAFF_L')}
        />
      </Modal>

      {/* //second modal */}
      <Modal
        open={isSecondModalOpen}
        onCancel={handleCloseSecondModal}
        footer={null}
        closable={false}
        centered
        className="assign-staff-modal-main second"
      >
        {AssignStaffHeaderModal()}
        {AssignStaffFormItemModal()}
        <AssignStaffModalFooter
          onClick={handleAssignStaff}
          isDisabled={!role}
          buttonText={trans('ASSIGN_STAFF_L')}
        />
      </Modal>
    </>
  );
};

// Wrap the component with App to provide message context
const AssignStaffModalWithApp = (props) => (
  <App>
    <AssignStaffModal {...props} />
  </App>
);

export default AssignStaffModalWithApp;
